import { Spacer } from "../Spacer";
import { TextCapitalized } from "../text/TextCapitalized";
import { colors } from "../../../styles/figmaColors";
import { typographies } from "../../../styles/figmaTypographies";
import {
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  Label,
  ResponsiveContainer,
} from "recharts";
import styled from "styled-components";
import { CATEGORY_COLORS } from "./ChartColors";

export interface PieChartData {
  name: string;
  value: number;
  color?: string;
  amount?: string;
}

interface PieChartDSProps {
  categoryData: PieChartData[];
  unit: string;
  displayPercentageLegend: boolean;
  centerLabel?: { value: string; label: string };
}

interface TooltipPayload {
  name: string;
  value: number;
}

interface LegendPayload {
  value: string;
  color: string;
  payload?: { percentage: number; amount?: string };
}

export const PieChartDS = (props: PieChartDSProps) => {
  const categoryDataWithColors = props.categoryData.map((category, index) => ({
    ...category,
    color:
      category.color ?? colors[CATEGORY_COLORS[index % CATEGORY_COLORS.length]],
    percentage: category.value,
  }));

  return (
    <ResponsiveContainer>
      <PieChart width={500} height={700}>
        <Pie
          data={categoryDataWithColors}
          dataKey="value"
          innerRadius={60}
          outerRadius={90}
          labelLine={false}
        >
          {categoryDataWithColors.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
          {props.centerLabel && (
            <Label
              position="center"
              content={
                <CenterLabel
                  value={props.centerLabel.value}
                  label={props.centerLabel.label}
                />
              }
            />
          )}
        </Pie>
        <Tooltip content={<CustomTooltip unit={props.unit} />} />
        <Legend
          layout="vertical"
          verticalAlign="middle"
          align="left"
          content={
            <CustomLegend displayPercentage={props.displayPercentageLegend} />
          }
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

const CenterLabel = (props: {
  viewBox?: { cx?: number; cy?: number };
  value: string;
  label: string;
}) => {
  const cx = props.viewBox?.cx ?? 0;
  const cy = props.viewBox?.cy ?? 0;
  return (
    <g>
      <text
        x={cx}
        y={cy - 4}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="20"
        fontWeight="600"
        fill={colors["colors/text/black"]}
      >
        {props.value}
      </text>
      <text
        x={cx}
        y={cy + 18}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="13"
        fill={colors["colors/text/lightGrey"]}
      >
        {props.label}
      </text>
    </g>
  );
};

const CustomTooltip = ({
  active,
  payload,
  unit,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
  unit: string;
}) => {
  if (active && payload && payload.length > 0) {
    return (
      <StyledTooltip>
        <Spacer y={0.75} />
        <StyledRow>
          <Spacer x={0.75} />
          {payload.map((entry, index) => (
            <div key={index + "tooltip"}>
              <TextCapitalized>{entry.name} :</TextCapitalized>
              <Spacer y={0.5} />
              <TextCapitalized>
                {Number(entry.value).toFixed(2)} {unit}
              </TextCapitalized>
            </div>
          ))}
          <Spacer x={0.75} />
        </StyledRow>
        <Spacer y={0.75} />
      </StyledTooltip>
    );
  }
  return null;
};

const CustomLegend = ({
  payload,
  displayPercentage,
}: {
  payload?: LegendPayload[];
  displayPercentage: boolean;
}) => {
  if (payload && payload.length > 0) {
    return (
      <StyledLegend>
        {payload.map((entry, index) => {
          const percentage = entry.payload?.percentage ?? 0;
          const amount = entry.payload?.amount;
          return (
            <div key={index + "legend"}>
              <StyledRow>
                <StyledDot color={entry.color}></StyledDot>
                <Spacer x={0.5} />
                <TextCapitalized>{entry.value}</TextCapitalized>
                {displayPercentage && (
                  <>
                    <Spacer x={0.5} />
                    <StyledPercentage>
                      <TextCapitalized>
                        ({Number(percentage).toFixed(2)}%)
                      </TextCapitalized>
                    </StyledPercentage>
                  </>
                )}
                {amount && (
                  <>
                    <Spacer x={0.5} />
                    <TextCapitalized>{amount}</TextCapitalized>
                  </>
                )}
              </StyledRow>
              <Spacer y={0.5} />
            </div>
          );
        })}
      </StyledLegend>
    );
  }
  return null;
};

const StyledTooltip = styled.div`
  display: flex;
  flex-direction: column;
  ${typographies["Body/L"]}
  color: ${colors["colors/text/black"]};
  background-color: ${colors["colors/surfaces/background/background_level0"]};
  border-radius: 0.5rem;
  border: 0.0625rem solid ${colors["colors/borders/cards/cards"]};
`;

const StyledLegend = styled.div`
  display: flex;
  flex-direction: column;
  ${typographies["Body/S"]}
  color: ${colors["colors/text/black"]};
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
`;

const StyledPercentage = styled.div`
  color: ${colors["colors/text/lightGrey"]};
`;

const StyledDot = styled.div<{ color?: string }>`
  display: flex;
  height: 1rem;
  width: 1rem;
  border-radius: 50%;
  background-color: ${({ color }) =>
    color ? color : `${colors["colors/text/black"]}`};
`;
